import type { PensionGoalFormType } from "Forms/Schemas/Goals/pensionGoalFormSchema";
import { AmountFrequency, type PensionGoalData, type PensionGoalModeling, TimeRangeType } from "Infrastructure/ApiZfp/Schema";
import { format } from "date-fns";

const CURRENCY_CODE = "CZK";

// Masked inputs hand back strings (and empty strings while a field is untouched), even though the form type
// declares numbers — coerce defensively so a half-filled form still produces a valid preview request.
const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toApiDate = (value: Date | null | undefined) => (value ? format(value, "yyyy-MM-dd") : null);

export const buildPensionGoalData = (values: PensionGoalFormType): PensionGoalData => ({
  targetDate: toApiDate(values.statePensionStart),
  retirementStartDate: toApiDate(values.statePensionStart),
  isInfinityRentRequired: values.infinitePension,
  isRetirementPensionExpected: values.includeStatePension,
  retirementStartAge: { value: toNumber(values.privatePensionStartAgeMonths), type: TimeRangeType.month },
  retirementPayoutInterval: { value: toNumber(values.privatePensionDuration), type: TimeRangeType.year },
  employmentDuration: { value: toNumber(values.yearsWorked), type: TimeRangeType.year },
  grossSalaryAmount: { value: toNumber(values.grossIncomeAmount), currencyCode: CURRENCY_CODE, frequency: AmountFrequency.monthly },
  netSalaryAmount: { value: toNumber(values.netIncomeAmount), currencyCode: CURRENCY_CODE, frequency: AmountFrequency.monthly },
  expectedRetirementAmount: {
    value: toNumber(values.statePensionAmount),
    currencyCode: CURRENCY_CODE,
    frequency: AmountFrequency.monthly,
  },
  rentAmount: { value: toNumber(values.privatePensionAmount), currencyCode: CURRENCY_CODE, frequency: AmountFrequency.monthly },
});

export const buildPensionGoalModeling = (values: PensionGoalFormType): PensionGoalModeling => ({
  investmentAmount: {
    value: toNumber(values.monthlyContributionWithFee),
    currencyCode: CURRENCY_CODE,
    frequency: AmountFrequency.monthly,
  },
  targetAmount: { value: toNumber(values.targetAmount), currencyCode: CURRENCY_CODE, frequency: AmountFrequency.one_time },
  isTargetAmountManual: !!values.isTargetAmountManual,
  isInvestmentAmountManual: !!values.isInvestmentAmountManual,
  appreciationRate: toNumber(values.interestRate),
  payoutAppreciationRate: toNumber(values.pensionInterestRate),
  inflation: { enabled: !!values.includeInflation, rate: toNumber(values.inflationRate) },
  fee: { enabled: !!values.includeFee, rate: toNumber(values.feeRate) },
});
